import ArticleLayout from '@/components/feature/ArticleLayout';
import { SITE_URL } from '@/constants/site';

const articleJsonLd = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Sugargoo Review 2026 — Honest Platform Analysis & Ratings",
  "description": "An in-depth, unbiased Sugargoo platform review covering user interface, product sourcing quality, shipping performance, customer support responsiveness, and overall value compared to alternative shopping agents in 2026.",
  "image": "https://readdy.ai/api/search-image?query=Clean%20modern%20desk%20with%20open%20laptop%20displaying%20elegant%20ecommerce%20platform%20dashboard%20interface%2C%20smartphone%20beside%20it%20showing%20a%20shopping%20app%20with%20product%20thumbnails%2C%20soft%20natural%20window%20light%20from%20the%20left%2C%20warm%20cream%20and%20beige%20tones%2C%20professional%20review%20and%20evaluation%20aesthetic%2C%20editorial%20tech%20lifestyle%20photography%20with%20balanced%20composition&width=1200&height=600&seq=article-review-hero-01&orientation=landscape",
  "datePublished": "2026-07-25",
  "dateModified": "2026-07-25",
  "author": { "@type": "Organization", "name": "Sugargoo Spreadsheet Hub" },
  "publisher": { "@type": "Organization", "name": "Sugargoo Spreadsheet Hub", "url": SITE_URL },
  "mainEntityOfPage": { "@type": "WebPage", "@id": SITE_URL + "/sugargoo-review" },
  "inLanguage": "en"
};

export default function ReviewPage() {
  return (
    <ArticleLayout
      title="Sugargoo Review 2026 — Honest Platform Analysis & Ratings"
      subtitle="An in-depth, unbiased Sugargoo platform review covering user interface, product sourcing quality, shipping performance, customer support responsiveness, and overall value compared to alternative shopping agents in 2026."
      featuredImage="https://readdy.ai/api/search-image?query=Clean%20modern%20desk%20with%20open%20laptop%20displaying%20elegant%20ecommerce%20platform%20dashboard%20interface%2C%20smartphone%20beside%20it%20showing%20a%20shopping%20app%20with%20product%20thumbnails%2C%20soft%20natural%20window%20light%20from%20the%20left%2C%20warm%20cream%20and%20beige%20tones%2C%20professional%20review%20and%20evaluation%20aesthetic%2C%20editorial%20tech%20lifestyle%20photography%20with%20balanced%20composition&width=1200&height=600&seq=article-review-hero-01&orientation=landscape"
      featuredImageAlt="Sugargoo Platform Review 2026"
      articleDate="July 25, 2026"
      readingTime="9 minutes"
      category="Platform Review"
      jsonLd={articleJsonLd}
    >
      <section>
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-8 mb-4">
          Sugargoo Platform Overview
        </h2>
        <p>
          Sugargoo has established itself as one of the major shopping agents connecting international buyers with Chinese e-commerce platforms. The service acts as a middle layer — you browse products on Taobao, Weidian, or 1688, buy through Sugargoo, have items shipped to their warehouse for inspection, and then forwarded internationally to your address. This review evaluates the platform across key dimensions that matter most to shoppers, drawing from community experiences and platform performance data.
        </p>
      </section>

      <section>
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-8 mb-4">
          Platform Ratings at a Glance
        </h2>
        <div className="overflow-x-auto my-6">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-background-200">
                <th className="text-left py-3 px-4 font-semibold text-foreground-950">Category</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-950">Rating</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-950">Notes</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Product Search & Discovery', '8.5/10', 'Smooth link-pasting, good search integration'],
                ['Warehouse Service', '8/10', 'Reliable QC photos, reasonable storage terms'],
                ['Shipping Options', '8/10', 'Good variety of lines, competitive rates'],
                ['Customer Support', '7/10', 'Adequate but can be slow during peak times'],
                ['Platform UI/UX', '7.5/10', 'Functional design, mobile app available'],
                ['Pricing Transparency', '7.5/10', 'Clear fees but exchange rates vary'],
                ['Community Integration', '9/10', 'Strong Sugargoo Spreadsheet ecosystem'],
                ['Overall Value', '8/10', 'Solid choice for international shoppers'],
              ].map((row, i) => (
                <tr key={i} className={`border-b border-background-100 ${i % 2 === 0 ? 'bg-background-50/50' : ''}`}>
                  {row.map((cell, j) => (
                    <td key={j} className={`py-3 px-4 ${j === 0 ? 'font-medium text-foreground-800' : j === 1 ? 'font-semibold text-primary-600' : 'text-foreground-600'}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-8 mb-4">
          Strengths: Where Sugargoo Excels
        </h2>
        <div className="space-y-4 my-6">
          {[
            { title: 'Strong Warehouse Infrastructure', desc: 'The warehouse service is Sugargoo\'s standout feature. QC photos are consistent and useful, the 180-day free storage period is generous by industry standards, and the consolidation process is straightforward. For shoppers who build large hauls over time, this infrastructure is invaluable.' },
            { title: 'Active Community Ecosystem', desc: 'The Sugargoo Spreadsheet community is one of the most active in the shopping agent space. Thousands of users share product links, QC photos, reviews, and shipping experiences, creating a self-reinforcing cycle of trust and quality verification that benefits everyone — especially newcomers.' },
            { title: 'Competitive Shipping Rates', desc: 'Sugargoo\'s shipping rates are competitive with other major agents. The platform offers a wide range of shipping lines — from budget sea freight to express air courier — giving shoppers flexibility to balance speed against cost based on their specific needs.' },
          ].map((item, i) => (
            <div key={i} className="p-4 rounded-xl bg-background-100 border border-background-200/70">
              <h4 className="font-heading text-base font-semibold text-foreground-950 mb-2 flex items-center gap-2">
                <i className="ri-thumb-up-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                {item.title}
              </h4>
              <p className="text-sm text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-8 mb-4">
          Weaknesses: Room for Improvement
        </h2>
        <div className="space-y-4 my-6">
          {[
            { title: 'Customer Support Responsiveness', desc: 'While generally helpful when they do respond, customer support response times can be frustratingly slow during peak shopping periods. English-language support in particular can take 24–48 hours for complex inquiries, which is a notable pain point for international users on different time zones.' },
            { title: 'Website User Experience', desc: 'The Sugargoo website is functional but not particularly polished. Navigation can feel clunky, some features are buried in menus, and the mobile experience — while improved with a dedicated app — still has rough edges compared to modern e-commerce platforms.' },
          ].map((item, i) => (
            <div key={i} className="p-4 rounded-xl bg-background-100 border border-background-200/70">
              <h4 className="font-heading text-base font-semibold text-foreground-950 mb-2 flex items-center gap-2">
                <i className="ri-thumb-down-line text-accent-500 w-5 h-5 flex items-center justify-center"></i>
                {item.title}
              </h4>
              <p className="text-sm text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-8 mb-4">
          Who Should Use Sugargoo?
        </h2>
        <p>
          Sugargoo is an excellent choice for international shoppers who want a reliable agent with strong warehouse services and an active community ecosystem. It is particularly well-suited for shoppers building medium to large hauls who will benefit from the free storage period, QC photo service, and consolidation features. Beginners will find the Sugargoo Spreadsheet community especially valuable as a source of pre-vetted product links and guidance.
        </p>
        <p>
          Shoppers who prioritize lightning-fast customer support or the most polished user interface may want to compare Sugargoo against alternatives. However, for the majority of international shoppers — especially those focused on streetwear, sneakers, and fashion accessories — Sugargoo with a quality spreadsheet is a combination that is hard to beat in terms of overall value.
        </p>
      </section>
    </ArticleLayout>
  );
}